﻿/* eslint-disable indent */
import { ApiCaller, FullfilledApiResponse } from 'apis/ApiCaller';
import { LicenseType, TransactionType } from 'types';
import { getFormattedDateApi } from 'utils';

/** Valid sort column names accepted by the Transaction GetAllPaged endpoint. */
export type TransactionSortField =
    | 'AssignmentID'
    | 'CreateDate'
    | 'Name'
    | 'Partner'
    | 'CreatedBy'
    | 'Instructor'
    | 'StartDate'
    | 'EndDate'
    | 'TransactionType'
    | 'LicenseType'
    | 'UserId'
    | 'NumStudents'
    | 'OrderExpiration'
    | 'Codes'
    | 'Archived'
    | 'LicensesCSV'
    | 'UsesRemaining';

export interface TransactionPartner {
  Id: number;
  Name: string;
}

export interface Transaction {
  Archived?: boolean;
  Codes: string; // Codes (new name for old Access Codes) that can be used on the Access Code.
  CreatedBy: string;
  CreatedDate: Date;
  CreatedDateString: string;
  ExpirationDate: Date | null;
  ExpirationDateString: string | null;
  Groups: string;
  HasBulbAccess?: boolean;
  HasXED?: boolean;
  Id: number;
  Identifier: string;
  IndividualMaxStudents: number;
  Instructor: string;
  LeastOrderExpirationDate: Date;
  LeastOrderExpirationDateString: string;
  LicenseName: string;
  LicensesCSV?: string;
  LicenseType: LicenseType;
  Name: string;
  NumOfStudents: number;
  OnCreateSingleUse?: { amount: number; singleUse: boolean };
  Partner?: TransactionPartner;
  PartnerId?: number;
  RestrictingOrder?: number;
  StartDate: Date | undefined;
  StartDateString: string;
  TransactionType: TransactionType;
  UserId: string;
  UsesRemaining?: number;
}

export interface TransactionDetails extends Transaction {
  AllowReview?: boolean;
  AllowStudyGuide?: boolean;
  AssociateProduct?: boolean;
  CloudRemaining?: number;
  CourseGateType?: CourseGating;
  CourseIds?: number[];
  CoursesRemaining?: number;
  CreatedById?: number;
  EBookIds?: number[];
  EBooksRemaining?: number;
  HasBulbAccess?: boolean;
  HasCloud?: boolean;
  HasCourses?: boolean;
  HasEBooks?: boolean;
  HasTests?: boolean;
  IsDuplicated: boolean;
  IsUnlimited: boolean;
  LeastOrderExpiratiDate?: Date | undefined;
  Notes: string;
  NumOfBooks: number;
  NumOfCourses: number;
  NumOfTests: number;
  NumXEDAccess: number;
  OrderIds?: number[];
  PendingBooks: SelectedContentReference[];
  PendingCourses: SelectedContentReference[];
  PendingTests: SelectedContentReference[];
  TestIds?: number[];
  TestingMode: boolean;
  TestsRemaining: number;
  TrainingMode: boolean;
  UsageDays?: number | undefined;
  XEDExternalId: string;
}

export interface TransactionReference {
  Active: boolean;
  ContentAccessTypeId: number;
  ContentAccessTypeName: string;
  CreatedDate: Date;
  ExpirationDate: Date;
  Id: number;
  LicenseType: LicenseType;
  ModifiedDate: Date;
  Name: string;
  StudentContentAccessId: number;
  StudentId: number;
  TransactionId: number;
  TransactionType: TransactionType;
}

export interface AssignedStudent {
  Active: boolean;
  CloudRemaining: number;
  ContentAccessTypeId: number;
  ContentAccessTypeName: string;
  CoursesRemaining: number;
  EBooksRemaining: number;
  ExpirationDate: Date;
  ExpirationDateString: string;
  Firstname: string;
  Fullname: string;
  HasBulbAccess: boolean;
  HasCloud: boolean;
  HasCourses: boolean;
  HasEBooks: boolean;
  HasTests: boolean;
  Id: number;
  Lastname: string;
  StudentContentAccessId: number;
  TestsRemaining: number;
  TotalUsesRemaining: number;
  Username: string;
}

export interface AssignedAccessCode {
  AccessCodeTitle: string;
  Active: boolean;
  CodeType: CodeType;
  CreatedAt: Date;
  ExpirationDate: Date | undefined;
  ExpirationDateString: string;
  ExpiresAfterDays: number;
  Id: number;
  Redeemed: number;
  Remaining: number | undefined;
}

export interface AccessCodeValidationResult {
  Code: string;
  Found: boolean;
  Remaining: number;
  TransactionId: number;
}

export interface CourseGating {
  Description: string;
  Id: number;

  // Specific for radio button component.
  label: string;
  Name: string;
  NameAndDescription: string;

  title: string;
  TranslatedName: string;
  value: number;
}

export enum CodeType {
  Single = 1,
  Multiple,
}

export enum DeliverableType {
  Test = 1,
  Book = 2,
  Course = 3,
  XedCourse = 8,
}

export enum CourseType {
  Gmetrix = 1,
  MediaPlus,
  JasperActive,
  LayoutModel,
  TrainingProgram,
  Survey,
  CodeAvengers,
  Scorm,
  XEDCourse,
}

export interface TreeViewProps {
  expanded: boolean;
  key: string;
  text: string;
}

export interface CategoryTree extends TreeViewProps {
  CategoryIcon: string;
  CategoryId: number;
  items: ProductTree[];
  OrderIds: number[];
}

export interface ProductTree extends TreeViewProps {
  items: DeliverableTree[];
  ProductBackgroundColor: string;
  ProductIcon: string;
  ProductId: number;
}

export interface DeliverableTree extends TreeViewProps {
  DeliverableTypeId: number;
  IsCustomTest?: boolean;
  items: ContentDeliverable[];
}

export interface ContentDeliverable {

  AllowReview: boolean;

  // Book
  AuthorName?: string;
  CategoryColor: string;
  CategoryIcon: string;
  CategoryId: number;

  CategoryName: string;
  CertificateChecked?: boolean;
  CertificationId: number;
  CertificationName: string;

  //Tree view properties
  check: string;
  Checked?: boolean;
  CloudAvailable: boolean;
  ContentHours: number;
  CourseGateType?: number;
  CourseType: CourseType;
  DeliverableTypeId:
    | DeliverableType.Test
    | DeliverableType.Book
    | DeliverableType.Course
    | DeliverableType.XedCourse;
  ExpirationDate: Date | null;
  ExpirationDateString: string | null;
  Id: number; // Testing mode id
  IsCustomTest: boolean;
  IsProctored: boolean;
  key: string;

  // Test
  MaxAttempts: number;
  Name: string;
  OrderIds: number[];
  PassingScore?: number;

  // PendingAdd is a flag for pending product/deliverables being added the the Transaction or deleted.
  PendingAdd: boolean;

  // PendingDelete is a flag for pending product/deliverable level settings to be deleted.
  PendingDelete: boolean;

  // PendingUpdate is a flag for pending product/deliverable level settings to be updated.
  PendingUpdate: boolean;

  ProctorTypesAllowed: number[];

  ProductColor: string;

  ProductColorIcon?: string;
  ProductIcon: string;
  ProductIconWide?: string;
  ProductId: number;
  ProductName: string;
  QuestionCount?: number;

  ResourceCount?: number;
  RestrictedOrderId: number;
  StartDate: Date | undefined;
  StartDateString: string;
  TestAttempts: number;
  TestingMode: boolean;
  text: string;
  TimeInMinutes?: number;

  // Removing Training mode from test. Used ONLY for content setting level training mode checkbox.
  TimeRequired?: number;

  TrainingMode: boolean;

  //-------------------
  TransactionId: number;

  // Course
  VendorId?: number;
  VendorLogo: string;

  VendorName: string;
}

export type LicenseReturnable = {
  allLicenseHistory: License[];
  licenses: License[];
};

export interface License {

  AllowAssociate: number;
  AllowOffline: boolean;
  BulbAccessUses: number;
  Checked: boolean;
  CloudAccessUses: number;
  Comment: string;
  CoursesSeats: number;
  CoursesUsersPerSeat: number;
  CourseUses: number;
  EbookSeats: number;
  EbookUsersPerSeat: number;
  EbookUses: number;
  HasBulbAccess: boolean;

  //Id and OrderId are the same.
  Id: number;
  IsOffline: boolean;
  IsUnlimited: boolean;
  LicenseActive: boolean;
  LicenseBulbAccessMaxUses: number;
  LicenseBulbAccessNumUses: number;
  LicenseCloudAccessMaxUses: number;
  LicenseCloudAccessNumUses: number;
  LicenseCoursesMaxUses: number;
  LicenseCoursesNumUses: number;
  LicenseDaysExpire: number;
  LicenseEbookMaxUses: number;
  LicenseEbookNumUses: number;
  LicenseId: number;
  LicenseName: string;
  LicensePracticeTestsMaxUses: number;
  LicensePracticeTestsNumUses: number;
  LicenseType: number;
  LicenseTypeString: string;
  LicenseXEDMaxUses: number;
  LicenseXEDNumUses: number;
  NumAvailable: number;
  NumGranted: number;
  NumRequeseted: number;
  OrderActive: boolean;
  OrderControl: string;
  OrderCreateDate: Date;
  OrderCreateDateString: string;
  OrderExpireDate: Date;
  OrderExpireDateString: string;
  OrderId: number;
  OrderModifyDate: Date;
  OrderModifyDateString: string;
  OrderStartDate: Date;
  OrderStartDateString: string;
  OrderStatus: number;
  PracticeTestsSeats: number;
  PracticeTestsUsersPerSeat: number;
  PracticeTestUses: number;
  StringCourses: string;
  UsageDays: number;
  XEDUses: number;
}

export interface SelectedContent {
  DeliverableTypeId: DeliverableType.Test | DeliverableType.Book | DeliverableType.Course | DeliverableType.XedCourse;
  Id: number;
  Name: string;
  NonAvailableLicenses?: SelectedContent[];
  OrderIds: number[];
}

export interface SelectedContentReference {
  id: number;
  restrictedOrderId: number;
}

export interface PendingLicenseRestriction {
  ConflictingDeliverableList: SelectedContent[];
  LicenseRestrictions: License[];
  NonAvailableLicenseList: NonAvailableLicense[];
  OrderIds: number[];
  Valid: boolean;
}

export interface NonAvailableLicense {
  content: SelectedContent;
  license: License;
}

export interface ProctorTypes {
  Description: string;
  Id: number;
  Name: string;
}

export interface MostRestrictiveLicense {
  avaliableStudentsAllowed: number;
  license: License;
}

export interface CourseResource {
  CourseId: number;
  Description: string;
  Id: number;
  Name: string;
  ResourceTypeId: CourseResourceType;
  URL: string;
}

export enum CourseResourceType {
  PracticeTest = 0,
  Video,
  PDF,
  WordDocument,
  PowerpointDocument,
  ExcelDocument,
  ZipFile,
  Glossary,
  MediaPlusSSO,
  WebContent,
  JasperActiveSSO,
  FileUpload,
  Text,
  Essay,
  Course,
  LiveEvent,
  Question,
  Affidavit,
  BranchingSlideDeck,
  LTI,
  LtiResourceLinkRequest,
}

export type PaginatedResult<T> = {
  Items: T[];
  Page: number;
  PageSize: number;
  TotalCount: number;
};

/**
 * Represents an api handler for the TransactionController.
 * */
export class TransactionApi {

  private static apiCaller = new ApiCaller('Transactions');

  public static Get(transactionId: number, partnerId: number | null = null): Promise<TransactionDetails> {

    return this.apiCaller.GET<TransactionDetails>('Get', { partnerId, transactionId });

}

  public static GetAll(
    cultureId = 1,
    studentFilterCSV?: string,
    codesToInclude?: number,
    objectId?: number
  ): Promise<Transaction[]> {

    return this.apiCaller.GET<Transaction[]>('GetAll', {
      codesToInclude: codesToInclude ?? 10,
      cultureId,
      objectId: objectId ?? null,
      studentFilterCSV: studentFilterCSV ?? ''
    });

}

  /**
   * Gets only the access codes (transactions) that grant the resource identified by the given
   * deliverable control (gmxresourcekey). Used by the Integrations page so a user cannot pick an
   * access code that does not include the selected resource.
   *
   * @deprecated Superseded by GetByResourcePaged, which pages on the server. NO caller remains -
   * both access-code lookup modals now use the paged endpoint. It is kept for one release only, so
   * that reverting the two modal files is a sufficient rollback if the paged path misbehaves in
   * production. Delete it, TransactionsController.GetByResource and
   * DaoTransaction.GetTransactionsByResource together once the paged path has shipped cleanly.
   */
  public static GetByResource(
    deliverableControl: string,
    cultureId = 1
  ): Promise<Transaction[]> {

    return this.apiCaller.GET<Transaction[]>('GetByResource', {
      cultureId,
      deliverableControl
    });

}

  /**
   * Paged lookup of the access codes (transactions) that grant the resource identified by the
   * given deliverable control (gmxresourcekey). Same paging/sorting/searching contract as
   * GetAllPaged; the deliverable filter is applied inside the stored procedure, so only one page
   * is ever materialised. Supersedes GetByResource, which returned every match.
   *
   * The server resolves the deliverable control to a test or course itself, exactly as GetByResource
   * does, so the caller passes only the key.
   */
  public static GetByResourcePaged(
    deliverableControl: string,
    page = 1,
    pageSize = 25,
    sortField: TransactionSortField | '' = '',
    sortDir = '',
    searchTerm = '',
    cultureId = 1
  ): Promise<PaginatedResult<Transaction>> {

    return this.apiCaller.GET<PaginatedResult<Transaction>>('GetByResourcePaged', {
      cultureId,
      deliverableControl,
      page,
      pageSize,
      searchTerm,
      sortDir,
      sortField
    });

}

  public static GetAllPaged(
    page = 1,
    pageSize = 25,
    sortField: TransactionSortField | '' = '',
    sortDir = '',
    searchTerm = '',
    showArchived = false,
    showMyAccessCodes = false,
    cultureId = 1
  ): Promise<PaginatedResult<Transaction>> {

    return this.apiCaller.GET<PaginatedResult<Transaction>>('GetAllPaged', {
      cultureId,
      page,
      pageSize,
      searchTerm,
      showArchived,
      showMyAccessCodes,
      sortDir,
      sortField
    });

}

  public static GetStudentTransactions(
    studentId: number
  ): Promise<TransactionReference[]> {

    return this.apiCaller.GET<TransactionReference[]>(
      'GetStudentTransactions',
      { studentId }
    );

}

  public static GetStudentsByTransactionId(
    transactionId: number,
    cultureId = 1,
    partnerId: number | null = null
  ): Promise<AssignedStudent[]> {

    return this.apiCaller.GET<AssignedStudent[]>('GetStudentsByTransactionId', {
      cultureId,
      partnerId,
      transactionId
    });

}

  public static GetAccessCodesByTransactionId(
    transactionId: number,
    partnerId: number | null = null
  ): Promise<AssignedAccessCode[]> {

    return this.apiCaller.GET<AssignedAccessCode[]>(
      'GetAccessCodesByTransactionId',
      { partnerId, transactionId }
    );

}

  public static ValidateAccessCodes(
    codes: string[]
  ): Promise<AccessCodeValidationResult[]> {

    return this.apiCaller.POST<AccessCodeValidationResult[]>('ValidateAccessCodes', {
      codes
    });

}

  public static GetAccessCodesByTransactionIdPaged(
    transactionId: number,
    page = 1,
    pageSize = 25,
    sortField = '',
    sortDir = '',
    searchTerm = '',
    redeemedFilter = 1,
    partnerId: number | null = null
  ): Promise<PaginatedResult<AssignedAccessCode>> {

    return this.apiCaller.GET<PaginatedResult<AssignedAccessCode>>(
      'GetAccessCodesByTransactionIdPaged',
      {
        page,
        pageSize,
        partnerId,
        redeemedFilter,
        searchTerm,
        sortDir,
        sortField,
        transactionId
      }
    );

}

  public static GetCourseResourcesByCourseId(
    courseId: number,
    cultureId: number,
    partnerId: number
  ): Promise<CourseResource[]> {

    return this.apiCaller.GET<CourseResource[]>(
      'GetCourseResourcesByCourseId',
      { courseId, cultureId, partnerId }
    );

}

  public static GetAllCourseResources(
    groupId: number,
    cultureId: number
  ): Promise<CourseResource[][]> {

    return this.apiCaller.GET<CourseResource[][]>('GetAllCourseResources', {
      cultureId,
      groupId
    });

}

  public static GetLimitedContentByTransactionId(
    transactionId: number,
    partnerId: number | null = null
  ): Promise<ContentDeliverable[]> {

    return this.apiCaller.GET<ContentDeliverable[]>(
      'GetLimitedContentByTransactionId',
      { partnerId, transactionId }
    );

}

  public static GetAllContentForTransactions(
    testsOnly = false,
    flatListOnly = false,
    partnerId: number | null = null
  ): Promise<{ contentFlatList: ContentDeliverable[]; contentTreeView: any }> {

    return this.apiCaller.GET<{
      contentFlatList: ContentDeliverable[];
      contentTreeView: any;
    }>('GetAllContentForTransactions', { flatListOnly, partnerId, testsOnly });

}

  public static GetAllLicenses(partnerId: number | null = null): Promise<LicenseReturnable> {

    return this.apiCaller.GET<LicenseReturnable>('GetAllLicenses', { partnerId: partnerId ?? -1 });

}

  public static GetCourseGatingTypes(
    cultureId: number
  ): Promise<CourseGating[]> {

    return this.apiCaller.GET<CourseGating[]>('GetCourseGatingTypes', {
      cultureId
    });

}

  public static GetAllProctorTypes(
    testId: number,
    cultureId = 1
  ): Promise<ProctorTypes[]> {

    return this.apiCaller.GET<ProctorTypes[]>('GetAllProctorTypes', {
      cultureId,
      testId
    });

}

  public static Create({
    AllowReview,
    AllowStudyGuide,
    ExpirationDate,
    HasBulbAccess,
    HasCloud,
    Identifier,
    LeastOrderExpirationDate,
    Name,
    Notes,
    NumOfBooks,
    NumOfCourses,
    NumOfTests,
    NumXEDAccess,
    OrderIds,
    PendingCourses,
    StartDate,
    TestingMode,
    TrainingMode,
    TransactionType
  }: TransactionDetails): Promise<FullfilledApiResponse> {

    return this.apiCaller.POST<FullfilledApiResponse>('Create', {
      AllowReview,
      AllowStudyGuide,
      CourseIds: PendingCourses.map(course => course.id),
      ExpirationDate:
        ExpirationDate === null ? null : getFormattedDateApi(ExpirationDate),
      HasBulbAccess,
      HasCloudAccess: HasCloud,
      Identifier,
      LeastOrderExpirationDate,
      Name,
      Notes,
      NumOfBooks,
      NumOfCourses,
      NumOfTests,
      NumXEDAccess,
      OrderIds: OrderIds?.toString(),
      StartDate: getFormattedDateApi(StartDate),
      TestingMode,
      TrainingMode,
      TransactionType
    });

}

  public static Duplicate(
    transactionId: number,
    groupId: number | null = null,
    maxStudents: number | null = null,
    recalcStartDate: Date | null = null,
    avoidWeekends: boolean | null = null
  ): Promise<FullfilledApiResponse> {

    return this.apiCaller.POST<FullfilledApiResponse>('Duplicate', {
      avoidWeekends,
      groupId,
      maxStudents,
      recalcStartDate:
        recalcStartDate !== null ? getFormattedDateApi(recalcStartDate) : null,
      transactionId
    });

}

  public static AddContentToTransaction(
    transactionId: number,
    pendingTests: SelectedContentReference[],
    pendingBooks: SelectedContentReference[],
    pendingCourses: SelectedContentReference[],
    pendingContentSettings: ContentDeliverable[] = []
  ) {

    return this.apiCaller.POST('AddContentToTransaction', {
      pendingBooks,
      pendingContentSettings,
      pendingCourses,
      pendingTests,
      transactionId
    });

}

  public static AddStudentToTransaction(
    transactionIds: number[],
    studentIds: number[],
    partnerId?: number
  ): Promise<void> {

    return this.apiCaller.POST('AddStudentToTransaction', {
      partnerId,
      studentIds,
      transactionIds
    });

}

  public static AddGroupToTransaction(
    transactionIds: number[],
    groupIds: number[],
    partnerId?: number
  ): Promise<void> {

    return this.apiCaller.POST('AddGroupToTransaction', {
      groupIds,
      partnerId,
      transactionIds
    });

}

  public static CreateAccessCodeForTransaction(
    transactionId: number,
    identifier: string,
    codeType: number,
    expiresAfterDays: number,
    expirationDate: Date,
    numOfRedemptions: number,
    singleUserAccessCodeType = false
  ): Promise<FullfilledApiResponse> {

    return this.apiCaller.POST<FullfilledApiResponse>('CreateAccessCode', {
      codeType,
      expirationDate: getFormattedDateApi(expirationDate),
      expiresAfterDays,
      identifier,
      numOfRedemptions,
      singleUserAccessCodeType,
      transactionId
    });

}

  public static UpdateOneAccessCodeInTransaction({
    Active,
    ExpirationDate,
    ExpiresAfterDays,
    Id,
    Remaining
  }: AssignedAccessCode): Promise<void> {

    return this.apiCaller.PUT('UpdateoneAccessCodeInTransaction', {
      Active,
      ExpirationDate: getFormattedDateApi(ExpirationDate),
      ExpiresAfterDays,
      Id,
      Remaining
    });

}

  public static Update({
    AllowReview,
    AllowStudyGuide,
    CourseGateType,
    ExpirationDate,
    HasBulbAccess,
    HasCloud,
    Id,
    Identifier,
    LeastOrderExpirationDate,
    Name,
    Notes,
    NumOfBooks,
    NumOfCourses,
    NumOfTests,
    NumXEDAccess,
    OrderIds,
    PartnerId,
    StartDate,
    TestingMode,
    TestsRemaining,
    TrainingMode,
    TransactionType
  }: TransactionDetails): Promise<FullfilledApiResponse> {

    return this.apiCaller.PUT<FullfilledApiResponse>('Update', {
      AllowReview,
      AllowStudyGuide,
      CourseGateType,
      ExpirationDate: getFormattedDateApi(ExpirationDate),
      HasBulbAccess,
      HasCloudAccess: HasCloud,
      Id,
      Identifier,
      LeastOrderExpirationDate,
      Name,
      Notes,
      NumOfBooks: NumOfBooks ?? 0,
      NumOfCourses: NumOfCourses ?? 0,
      NumOfTests: NumOfTests ?? TestsRemaining,
      NumXEDAccess,
      OrderIds: OrderIds?.toString(),
      partnerId: PartnerId,
      StartDate: getFormattedDateApi(StartDate),
      TestingMode,
      TrainingMode,
      TransactionType
    });

}

  public static UpdateStudentExpirationByTransaction(
    transactionId: number,
    expirationDate: Date
  ): Promise<FullfilledApiResponse> {

    return this.apiCaller.PUT<FullfilledApiResponse>('UpdateStudentExpirationByTransaction', {
      expirationDate: getFormattedDateApi(expirationDate),
      transactionId
    });

}

  public static UpdateSingleContentSettings(
    contentSettingsList: ContentDeliverable[]
  ) {

    return this.apiCaller.PUT<FullfilledApiResponse>(
      'UpdateSingleContentSettings',
      { contentSettingsList }
    );

}

  public static DeleteContentFromTransaction(
    transactionId: number,
    testIds: number[],
    bookIds: number[],
    courseIds: number[]
  ) {

    return this.apiCaller.DELETE('DeleteContentFromTransaction', {
      bookIds: bookIds.toString(),
      courseIds: courseIds.toString(),
      testIds: testIds.toString(),
      transactionId
    });

}

  public static DeleteOneContentFromTransaction(
    transactionId: number,
    deliverableTypeId: number,
    id: number
  ) {

    return this.apiCaller.DELETE('DeleteOneContentFromTransaction', {
      id: id.toString(),
      deliverableTypeId,
      transactionId
    });

}

  public static RemoveStudentFromTransaction(
    studentContentAccessIds: number[]
  ): Promise<void> {

    return this.apiCaller.DELETE('RemoveStudentFromTransaction', {
      studentContentAccessIds
    });

}

  public static CreateXEDGroup(
    transactionId: number,
    userId: number,
    partnerId: number,
    listOfCourses: number[] = []
  ) {

    return this.apiCaller.GET<FullfilledApiResponse>('CreateXEDGroup', {
      listOfCourses,
      partnerId,
      transactionId,
      userId
    });

}

  public static SyncXEDGroup(
    transactionId: number,
    userId: number,
    partnerId: number,
    gmetrixSync = false,
    listOfCourses: string[] = []
  ) {

    return this.apiCaller.GET<FullfilledApiResponse>('SyncXEDGroup', {
      gmetrixSync,
      listOfCourses,
      partnerId,
      transactionId,
      userId
    });

}

  public static GetXEDEditGroupUrlByTransactionAndUserId(
    transactionId: number,
    userId: number
  ) {

    return this.apiCaller.GET<FullfilledApiResponse>(
      'GetXEDEditGroupUrlByTransactionAndUserId',
      {
        transactionId,
        userId
      }
    );

}

  public static GetInactiveOrdersByTransactionId(
    transactionId: number,
    cultureId = 1,
    partnerId: number | null = null
  ) {

    return this.apiCaller.GET<License[]>('GetInactiveOrdersByTransactionId', {
      cultureId,
      partnerId,
      transactionId
    });

}

  public static SetArchivedStatus(
    transactionId: number,
    archiveStatus: boolean
  ): Promise<FullfilledApiResponse> {

    return this.apiCaller.POST<FullfilledApiResponse>('SetArchivedStatus', {
      archiveStatus,
      transactionId
    });

}

}
